import { APP_CONFIG } from '../../../core/config/app-config.value';
import type { Asset, AssetPhoto, AssetStatus } from '../../../core/models/asset.model';
import type { AssetRowDto, CreateAssetRequestDto } from './inventory.dto';
import type { NewAssetDraft } from './inventory-api.service';

/**
 * Traduction entre le contrat HTTP et le modèle d'écran. Elle est isolée ici parce qu'elle porte
 * trois écarts que ni l'API ni les écrans ne doivent connaître :
 *
 * 1. les **statuts** voyagent en anglais (nom du membre de `AssetStatus`) et s'affichent en
 *    français — c'est la même règle RM-PIN-013 des deux côtés, dans deux langues ;
 * 2. les **dates** voyagent en ISO (`DateOnly`) et s'affichent en `JJ.MM.AAAA` ;
 * 3. certains champs d'écran **n'existent pas encore côté serveur** — ils sont ramenés à une valeur
 *    vide explicite, jamais à une donnée inventée.
 */

/**
 * Statuts, du nom d'énumération C# vers le libellé d'écran (RM-PIN-013). Le jeu est fermé des deux
 * côtés : une valeur inconnue est un bug de contrat, pas une donnée à afficher.
 */
const STATUS_FROM_API: Readonly<Record<string, AssetStatus>> = {
  Available: 'Disponible',
  Assigned: 'Assigné',
  AvailableToVerify: 'Disponible - À vérifier',
  UnderRepair: 'En réparation',
  Ordered: 'En commande',
  Decommissioned: 'Hors service',
  LostOrStolen: 'Perdu/Volé',
};

/** Sens inverse, pour le filtre de statut passé en query string à `GET /api/inventory/assets`. */
const STATUS_TO_API: Readonly<Record<AssetStatus, string>> = {
  Disponible: 'Available',
  Assigné: 'Assigned',
  'Disponible - À vérifier': 'AvailableToVerify',
  'En réparation': 'UnderRepair',
  'En commande': 'Ordered',
  'Hors service': 'Decommissioned',
  'Perdu/Volé': 'LostOrStolen',
};

export function statusToApi(status: AssetStatus): string {
  return STATUS_TO_API[status];
}

/** ISO `AAAA-MM-JJ` → `JJ.MM.AAAA`, le format que lisent `parseFrenchDate` et les écrans. */
export function isoToFrenchDate(value: string | null): string | null {
  if (!value) {
    return null;
  }

  // `DateOnly` se sérialise en `AAAA-MM-JJ`, mais un `DateTime` traînerait une partie horaire.
  const [date] = value.split('T');
  const [year, month, day] = date.split('-');

  return year && month && day ? `${day}.${month}.${year}` : null;
}

/** `JJ.MM.AAAA` → ISO `AAAA-MM-JJ`, attendu par `CreateAssetRequest.PurchaseDate`. */
export function frenchDateToIso(value: string | null): string | null {
  if (!value || !value.includes('.')) {
    return null;
  }

  const [day, month, year] = value.split('.');

  return year && month && day ? `${year}-${month}-${day}` : null;
}

/**
 * Libellé d'un détenteur que le backend ne désigne que par son identifiant. Tant que le module
 * Directory n'est pas branché, afficher le nom est impossible — mais laisser le champ vide serait
 * pire : la liste annoncerait « En stock » un matériel affecté. On affiche donc ce que l'on sait
 * vraiment, à savoir qu'un collaborateur identifié le détient.
 */
export function unresolvedHolderLabel(collaboratorId: string): string {
  return `Collaborateur ${collaboratorId.slice(0, 8)}`;
}

/**
 * Ligne d'API → fiche d'écran.
 *
 * Trois champs restent vides parce qu'ils n'ont aucune contrepartie serveur, et le dire est plus
 * utile que le combler :
 * - `history` — aucune entité de journal d'audit n'existe encore (RM-PIN-014 non implémentée) ;
 * - `assignedOn` — la date d'affectation n'est pas portée par la projection ;
 * - `holderType` — le backend n'affecte qu'à un collaborateur ; l'affectation à un lieu partagé
 *   (RM-PIN-018) n'est pas implémentée, donc un matériel affecté l'est forcément à une personne.
 */
export function assetFromDto(dto: AssetRowDto): Asset {
  const status = STATUS_FROM_API[dto.status];

  if (!status) {
    throw new Error(
      `Statut inconnu renvoyé par l'API : « ${dto.status} ». Le jeu de statuts attendus est fermé.`,
    );
  }

  return {
    id: dto.id,
    inventoryNumber: dto.inventoryNumber,
    serialNumber: dto.serialNumber,
    brand: dto.brand,
    name: dto.name,
    category: dto.category,
    status,
    holder: dto.holder ? unresolvedHolderLabel(dto.holder) : null,
    holderId: dto.holder,
    holderType: dto.holder ? 'collab' : null,
    location: dto.location,
    purchaseDate: isoToFrenchDate(dto.purchaseDate) ?? '—',
    warrantyMonths: dto.warrantyMonths,
    warrantyEnd: isoToFrenchDate(dto.warrantyEnd),
    value: dto.price,
    notes: dto.notes,
    supplier: dto.supplier ?? '—',
    assignedOn: null,
    photos: primaryPhotoOf(dto),
    history: [],
  };
}

/**
 * Photos affichables d'une fiche.
 *
 * La liste n'expose que **la photo principale**, par son identité (RM-PIN-122) : les octets ne
 * voyagent pas dans une liste, et aucun endpoint ne renvoie encore les métadonnées de la galerie.
 * On sert donc la vignette — le seul usage réellement couvert — et les autres photos restent
 * invisibles tant qu'un `GET /assets/{id}/photos` n'existe pas.
 *
 * Nom de fichier, type MIME et taille sont inconnus à ce stade : ils ne servent qu'au formulaire
 * de téléversement, qui travaille sur des fichiers locaux, jamais sur ceux qui viennent du serveur.
 */
function primaryPhotoOf(dto: AssetRowDto): readonly AssetPhoto[] {
  if (!dto.primaryPhotoId) {
    return [];
  }

  return [
    {
      id: dto.primaryPhotoId,
      fileName: '',
      url: `${APP_CONFIG.apiBaseUrl}/inventory/assets/${dto.id}/photos/${dto.primaryPhotoId}`,
      mimeType: '',
      sizeBytes: 0,
      isPrimary: true,
    },
  ];
}

/** Fiche saisie à l'écran → charge utile de création. */
export function createRequestFromDraft(draft: NewAssetDraft): CreateAssetRequestDto {
  return {
    category: draft.category?.trim() || null,
    brand: draft.brand?.trim() || null,
    name: draft.name?.trim() || null,
    serialNumber: draft.serialNumber?.trim() || null,
    location: draft.location?.trim() || null,
    purchaseDate: frenchDateToIso(draft.purchaseDate),
    warrantyMonths: draft.warrantyMonths,
    price: draft.value,
    supplier: draft.supplier?.trim() || null,
    notes: draft.notes?.trim() || null,
  };
}
